import type { ColumnDef } from '@tanstack/react-table'
import { formatDistanceToNow } from 'date-fns'
import {
  AvatarGroup,
  PriorityChip,
  StageBadge,
  TypeGlyph,
  UserAvatar,
} from '@/components/common/badges'
import { Badge } from '@/components/ui/badge'
import { CrossContextFlags } from '@/components/common/cross-context-flags'
import { IssueLink } from '@/components/common/issue-link'
import { LinkedPrIcons } from '@/components/common/linked-pr-icons'
import { rolloutKind } from '@/lib/stage'
import type { WorkIssue } from '@/types/github'

// Column definitions for IssueDataTable (TanStack ColumnDef). `meta` carries
// head/cell classNames; the table reads them in one place. Views compose their
// own column set from these without forking the table's row/Sheet behaviour.

const issueCol: ColumnDef<WorkIssue> = {
  id: 'issue',
  header: 'Issue',
  meta: { headClassName: 'w-[45%]' },
  cell: ({ row }) => {
    const issue = row.original
    return (
      <span className="flex items-center gap-2">
        <IssueLink
          number={issue.number}
          className="flex min-w-0 items-center gap-2 hover:underline focus-visible:underline"
        >
          <span className="text-faint-foreground w-11 shrink-0 text-right font-mono text-[11px] tabular-nums">
            #{issue.number}
          </span>
          <TypeGlyph typeName={issue.typeName} />
          <span title={issue.title} className="max-w-105 truncate font-medium">
            {issue.title}
          </span>
        </IssueLink>
        <CrossContextFlags issueId={issue.id} />
      </span>
    )
  },
}

const priorityCol: ColumnDef<WorkIssue> = {
  id: 'priority',
  header: 'Priority',
  cell: ({ row }) => <PriorityChip priority={row.original.priority} />,
}

const stageCol: ColumnDef<WorkIssue> = {
  id: 'stage',
  header: 'Stage',
  cell: ({ row }) => <StageBadge stage={row.original.stage} />,
}

const clientCol: ColumnDef<WorkIssue> = {
  id: 'client',
  header: 'Client',
  cell: ({ row }) =>
    row.original.client ? (
      <Badge variant="outline" className="px-1.5 py-px font-mono text-[10.5px] font-normal">
        {row.original.client}
      </Badge>
    ) : null,
}

const productCol: ColumnDef<WorkIssue> = {
  id: 'product',
  header: 'Product',
  meta: { cellClassName: 'text-muted-foreground text-xs' },
  cell: ({ row }) => row.original.product,
}

const assigneesCol: ColumnDef<WorkIssue> = {
  id: 'assignees',
  header: 'Assignees',
  cell: ({ row }) => <AvatarGroup users={row.original.assignees} />,
}

const authorCol: ColumnDef<WorkIssue> = {
  id: 'author',
  header: 'Author',
  cell: ({ row }) => {
    const { author, authorAvatarUrl } = row.original
    if (!author) return null
    return <UserAvatar user={{ login: author, name: null, avatarUrl: authorAvatarUrl ?? '' }} />
  },
}

const prsCol: ColumnDef<WorkIssue> = {
  id: 'prs',
  header: 'PR',
  cell: ({ row }) => <LinkedPrIcons prs={row.original.linkedPrs} />,
}

const closedCol: ColumnDef<WorkIssue> = {
  id: 'closed',
  header: 'Closed',
  meta: { cellClassName: 'text-muted-foreground text-xs whitespace-nowrap' },
  cell: ({ row }) => {
    const issue = row.original
    return (
      <>
        {issue.closedAt ? formatDistanceToNow(new Date(issue.closedAt), { addSuffix: true }) : '—'}
        {issue.stateReason === 'NOT_PLANNED' && (
          <span className="text-faint-foreground"> · not planned</span>
        )}
      </>
    )
  },
}

// Which post-deploy execution this is (datafix / migration); empty for non-rollouts.
const kindCol: ColumnDef<WorkIssue> = {
  id: 'kind',
  header: 'Kind',
  cell: ({ row }) => {
    const kind = rolloutKind(row.original.labels)
    return kind ? (
      <Badge variant="outline" className="px-1.5 py-px font-mono text-[10.5px] font-normal">
        {kind}
      </Badge>
    ) : null
  },
}

/** The standard open-list columns — IssueTable's default. */
export const ISSUE_COLUMNS: ColumnDef<WorkIssue>[] = [
  issueCol,
  priorityCol,
  stageCol,
  clientCol,
  productCol,
  prsCol,
  authorCol,
  assigneesCol,
]

/** Closed list: the standard set plus when each issue closed. */
export const CLOSED_COLUMNS: ColumnDef<WorkIssue>[] = [...ISSUE_COLUMNS, closedCol]

/** Rollouts: bot issues have no client/product/priority; show the kind, who's
 * driving a failed one (assignee), its stage, and when it verified (closed). */
export const ROLLOUT_COLUMNS: ColumnDef<WorkIssue>[] = [
  issueCol,
  kindCol,
  stageCol,
  assigneesCol,
  closedCol,
]
